// lib
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import {
  findVisualSelectionOffsetFromRangeNode,
  findRangeNodeForVisualSelectionOffset
} from '@aytm/lib/common/content-editable'

// styles
import '@aytm/lib/rfd-ui/styles/basic/autosized-input.scss'

// <Component />
export default class AutosizedInput extends Component {
  static propTypes = {
    text: PropTypes.string.isRequired,
    maxLength: PropTypes.number,
    multiline: PropTypes.bool,
    onChange: PropTypes.func,
    onUpdate: PropTypes.func,
    onKeyDown: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    debounce: PropTypes.number,
    placeholder: PropTypes.any,
    filter: PropTypes.func,
    readOnly: PropTypes.bool,
    contentEditable: PropTypes.any
  }

  constructor(...args) {
    super(...args);
    this.watchChanges = this.watchChanges.bind(this);
    this.unwatchChanges = this.unwatchChanges.bind(this);
    this.setText = this.setText.bind(this);
    this.sendUpdatedText = this.sendUpdatedText.bind(this);
    this.togglePlaceholder = this.togglePlaceholder.bind(this);
    this.onPlaceholderClick = this.onPlaceholderClick.bind(this);
    this.focus = this.focus.bind(this);
    this.getText = this.getText.bind(this);
    this.getHTML = this.getHTML.bind(this);
    this.setHTML = this.setHTML.bind(this);
    this.keepSelection = this.keepSelection.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
  }

  lastText = null
  divRef = React.createRef()
  placeholderRef = React.createRef()
  placeholderEnabled = false
  onChangeDebounceTimer = null
  divKey = `autosizedInput-${Math.round(Math.random() * 46656).toString(36)}`
  placeholderKey = `autosizedInputPlaceholder-${Math.round(Math.random() * 46656).toString(36)}`
  divMutationObserver =
    'undefined' === typeof MutationObserver
      ? null
      : new MutationObserver(() => {
          const text = this.getText()
          const { maxLength, filter, multiline } = this.props
          if ('function' === typeof filter) {
            // apply text filter
            const filteredText = filter(text, maxLength)
            if (filteredText !== text) {
              this.setText(filteredText, true)
              return
            }
          } else if (maxLength && text.length > maxLength) {
            // crop max length
            this.setText(text.slice(0, maxLength), true)
            return
          }
          if (!multiline && 0 <= text.indexOf('\n')) {
            this.setText(text.replace(/\n/g, ' '), true)
            return
          }
          const html = this.getHTML()
          if (0 <= html.indexOf('<')) {
            // remove tags from HTML if present
            this.setHTML(html.replace(/<[^<]*>/g, ''))
            return
          }
          this.togglePlaceholder('' == text)
          this.sendUpdatedText(text)
        })

  componentDidMount() {
    const { text } = this.props
    this.setText(text)
    this.watchChanges()
  }

  componentWillUnmount() {
    this.unwatchChanges()
  }

  shouldComponentUpdate(nextProps) {
    return nextProps.text !== this.props.text || nextProps.readOnly !== this.props.readOnly
  }

  componentDidUpdate(prevProps) {
    const { text, readOnly } = this.props
    this.setText(text)
    if (readOnly !== prevProps.readOnly) {
      if (readOnly) {
        this.unwatchChanges()
      } else {
        this.watchChanges()
      }
    }
  }

  watchChanges() {
    const { readOnly } = this.props
    if (!readOnly) {
      if (this.divMutationObserver && this.divRef.current) {
        this.divMutationObserver.observe(this.divRef.current, {
          characterData: true,
          childList: true,
          subtree: true
        })
      }
      if (this.divRef.current) {
        this.divRef.current.addEventListener('keydown', this.onKeyDown, true)
      }
    }
  }

  unwatchChanges() {
    if (this.divMutationObserver) {
      this.divMutationObserver.disconnect()
    }
    if (this.divRef.current) {
      this.divRef.current.removeEventListener('keydown', this.onKeyDown, true)
    }
  }

  setText(text, forceUpdate = false) {
    if (this.divRef.current && (forceUpdate || text !== this.lastText)) {
      this.keepSelection(() => {
        this.divRef.current.innerText = text
      })
      this.sendUpdatedText(text)
    }
    this.togglePlaceholder('' == text)
  }

  sendUpdatedText(text) {
    if (text !== this.lastText) {
      if (null != this.lastText) {
        const { onChange, onUpdate, debounce } = this.props
        if ('function' === typeof onUpdate) {
          onUpdate(text)
        }
        if ('function' === typeof onChange) {
          if (this.onChangeDebounceTimer) {
            clearTimeout(this.onChangeDebounceTimer)
            this.onChangeDebounceTimer = null
          }
          if (debounce) {
            this.onChangeDebounceTimer = setTimeout(() => {
              if ('function' === typeof onChange) {
                onChange(text)
              }
            }, debounce)
          } else {
            onChange(text)
          }
        }
      }
      this.lastText = text
    }
  }

  togglePlaceholder(enabled) {
    if (this.placeholderRef.current && this.placeholderEnabled != enabled) {
      this.placeholderEnabled = enabled
      if (enabled) {
        this.placeholderRef.current.classList.add('rfd-ui-autosized-input-placeholder-active')
      } else {
        this.placeholderRef.current.classList.remove('rfd-ui-autosized-input-placeholder-active')
      }
    }
  }

  onPlaceholderClick() {
    if (this.divRef.current) {
      this.divRef.current.focus()
    }
  }

  focus() {
    if (this.divRef.current) {
      this.divRef.current.focus()
    }
  }

  getText() {
    return this.divRef.current ? this.divRef.current.innerText : ''
  }

  getHTML() {
    return this.divRef.current ? this.divRef.current.innerHTML : ''
  }

  setHTML(html = '') {
    if (this.divRef.current) {
      //this.divRef.current.innerHTML = html
      this.keepSelection(() => {
        this.divRef.current.innerHTML = html
      })
    }
  }

  keepSelection(withFunction) {
    if (this.divRef.current) {
      if (this.divRef.current === document.activeElement) {
        const rangeBefore = window.getSelection().getRangeAt(0)
        const endOffsetBefore = findVisualSelectionOffsetFromRangeNode(
          this.divRef.current,
          rangeBefore.endContainer,
          rangeBefore.endOffset
        )
        withFunction()
        try {
          const rangeAfter = document.createRange()
          const { node, offset } = findRangeNodeForVisualSelectionOffset(this.divRef.current, endOffsetBefore)
          rangeAfter.setStart(node, offset)
          rangeAfter.collapse(true)
          const selectionAfter = window.getSelection()
          selectionAfter.removeAllRanges()
          selectionAfter.addRange(rangeAfter)
        } catch {
          // skip trying to set selection - content changed beyond recovery
        }
      } else {
        withFunction()
      }
    }
  }

  onKeyDown(event) {
    const { onKeyDown, multiline } = this.props
    if ('function' === typeof onKeyDown) {
      onKeyDown(event)
    }
    if (multiline && 'Enter' === event.key) {
      document.execCommand('insertLineBreak')
      event.preventDefault()
    }
    if (event && event.stopImmediatePropagation) {
      event.stopImmediatePropagation()
    } else if (event && event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
      event.nativeEvent.stopImmediatePropagation()
    }
  }

  onBlur(event) {
    const { onBlur, onChange } = this.props
    if (this.onChangeDebounceTimer) {
      clearTimeout(this.onChangeDebounceTimer)
      this.onChangeDebounceTimer = null
      if ('function' === typeof onChange) {
        const text = this.getText()
        this.sendUpdatedText(text)
      }
    }
    if ('function' === typeof onBlur) {
      onBlur(event)
    }
  }

  onFocus(event) {
    const { onFocus } = this.props
    if ('function' === typeof onFocus) {
      onFocus(event)
    }
  }

  render() {
    const {
      text,
      maxLength,
      multiline,
      onChange,
      onUpdate,
      placeholder,
      debounce,
      contentEditable,
      onKeyDown,
      onBlur,
      readOnly,
      filter,
      ...restProps
    } = this.props

    const inputComponent = (
      <div
        {...restProps}
        ref={this.divRef}
        contentEditable={!readOnly}
        onFocus={readOnly ? null : this.onFocus}
        onBlur={readOnly ? null : this.onBlur}
        key={this.divKey}
        className={restProps.className ? `rfd-ui-autosized-input ${restProps.className}` : 'rfd-ui-autosized-input'}
      />
    )

    const placeholderComponent = placeholder ? (
      <div
        className="rfd-ui-autosized-input-placeholder"
        ref={this.placeholderRef}
        key={this.placeholderKey}
        onClick={this.onPlaceholderClick}
      >
        {placeholder}
      </div>
    ) : null

    return placeholderComponent ? [placeholderComponent, inputComponent] : inputComponent
  }
}
